/** Mobile screens pin these exact hex values rather than the app's semantic tokens, so the port is pixel-faithful. */
export const M = {
  // Brand — volt primary for the main action (ink text on it), ink for emphasis.
  primary: '#D1FE17',
  primaryInk: '#171717',
  accent: '#171717',
  accentDark: '#000000',
  accentTint: '#F4F4F5',
  accentBadge: '#E4E4E7',
  navy: '#0A1628',
  // Surfaces
  bg: '#F9FAFB',
  card: '#FFFFFF',
  border: '#E5E7EB',
  divider: '#F3F4F6',
  muted: '#F3F4F6',
  // Text
  ink: '#111827',
  ink2: '#374151',
  ink3: '#6B7280',
  faint: '#9CA3AF',
  // Status
  green: '#15803D',
  greenDot: '#16A34A',
  greenTint: '#F0FDF4',
  green2: '#DCFCE7',
  amber: '#B45309',
  amberTint: '#FFFBEB',
  amber2: '#FEF3C7',
  red: '#B91C1C',
  redBright: '#DC2626',
  redTint: '#FEF2F2',
  red2: '#FEE2E2',
  // Neutral affordance glyph (e.g. row-disclosure chevrons).
  chevron: '#D1D5DB',
} as const;

/** Question-type badge palette keyed by a coarse type family. */
export type QTypeFamily = 'MC' | 'OE' | 'TF' | 'OTHER';

export function qTypeBadge(family: QTypeFamily): { bg: string; color: string; letter: string } {
  switch (family) {
    case 'OE':
      return { bg: '#EDE9FE', color: '#7C3AED', letter: 'OE' };
    case 'TF':
      return { bg: '#DCFCE7', color: '#15803D', letter: 'TF' };
    case 'MC':
      return { bg: '#DBEAFE', color: '#1D4ED8', letter: 'MC' };
    default:
      return { bg: '#DBEAFE', color: '#1D4ED8', letter: '?' };
  }
}

/** Anything not explicitly MC/TF/OE renders with its own initials, so nothing loses meaning. */
export function questionBadge(rawType?: string | null): { bg: string; color: string; letter: string } {
  const type = (rawType || '').toUpperCase();
  if (type.includes('TRUE') || type === 'TF' || type.includes('BOOLEAN')) return qTypeBadge('TF');
  if (
    type.includes('OPEN') ||
    type.includes('ESSAY') ||
    type.includes('SHORT') ||
    type.includes('TEXT') ||
    type.includes('MATH') ||
    type.includes('GRAPH')
  )
    return qTypeBadge('OE');
  if (type.includes('MULTIPLE') || type.includes('SINGLE') || type.includes('CHOICE') || type === 'MC')
    return qTypeBadge('MC');
  // Fall back to initials from the first two words.
  const letter =
    type
      .split(/[_\s-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('') || '?';
  return { bg: '#DBEAFE', color: '#1D4ED8', letter };
}

/** Score → tone color used across cards, tables and result rows. */
export function scoreColor(avg: number | null | undefined): string {
  if (avg == null) return M.faint;
  if (avg >= 78) return M.green;
  if (avg >= 60) return M.amber;
  return M.red;
}
